import { useEffect, useMemo, useRef, useState, type JSX } from 'react'
import {
  Bot,
  Brain,
  Database,
  Network,
  X,
  Pencil,
  Plus,
  Save,
  Trash2,
  Search,
  Wrench,
  FileText,
  Sparkles,
  Wand2,
  Loader2,
  Check,
  Cpu,
  ArrowRight,
  ArrowLeft } from 'lucide-react'
import { useConfirmDialog } from '../../ui/ConfirmDialog.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import { GuardrailPicker } from './GuardrailPicker.js'
import Select from '../../ui/Select.js'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface ToolDef {
  name: string
  category: string
}

export interface KnowledgeDef {
  id: string
  name: string
  category: string
}

export interface Agent {
  id: string
  slug: string
  name: string
  description: string
  /** Backend agent type: routing, response, data, translation, custom. */
  type: string
  category: 'Data' | 'Response' | 'Routing'
  color: string
  status: { enabled: boolean; isSystem: boolean }
  tools: string[]
  knowledge: string[]
  instructions: string | undefined
  /** The prompt template this agent uses, if any. When set, the agent's prompt
   * comes from the template (not the inline instructions). */
  promptId?: string | null
}

export interface EditAgentDrawerProps {
  /** When mode is 'create', agent is used as the initial template. */
  mode?: 'create' | 'edit'
  agent: Agent
  isOpen: boolean
  allTools: ToolDef[]
  allKnowledge: KnowledgeDef[]
  /** Which tab to show when the drawer opens. Defaults to 'basic'. */
  initialTab?: AgentDrawerTab
  onClose: () => void
  /** Called when Save Changes is clicked in edit mode. */
  onSave?: (updated: Agent, selectedGuardrailIds: string[]) => void | Promise<void>
  /** Called when Create Agent is clicked in create mode. */
  onCreate?: (data: CreateAgentData) => void | Promise<void>
  onDelete?: (id: string) => void | Promise<void>
}

export interface CreateAgentData {
  name: string
  slug: string
  description: string
  type: string
  systemPrompt: string
  promptId: string
  isEnabled: boolean
  tools: string[]
  knowledge: string[]
  guardrailIds: string[]
}

type AgentDrawerTab = 'basic' | 'type' | 'tools' | 'knowledge' | 'prompt'

const AGENT_TAB_ORDER: AgentDrawerTab[] = ['basic', 'type', 'tools', 'knowledge', 'prompt']

/** Brief used by the AI prompt generator. */
interface PromptBrief {
  task: string
  audience: string
  tone: 'professional' | 'friendly' | 'concise' | 'detailed'
  constraints: string
  goal: string
  rolePersona: string
  instructions: string
}

const DEFAULT_BRIEF: PromptBrief = {
  task: '',
  audience: '',
  tone: 'professional',
  constraints: '',
  goal: '',
  rolePersona: '',
  instructions: '',
}

type PromptSource = 'inline' | 'template'

interface PromptTemplate {
  id: string
  slug: string
  name: string
  content: string
  category: string
}

// -----------------------------------------------------------------------------
// Agent type presets — shared with the create wizard
// -----------------------------------------------------------------------------

const AGENT_TYPE_PRESETS: Record<string, { description: string; recommendedTools: string[]; systemPromptHint: string }> = {
  routing: {
    description: 'Classifies intent and routes conversations to the right agent, team, or campaign',
    recommendedTools: ['get_conversation_context', 'get_customer_context', 'get_campaigns', 'get_available_agents', 'get_routing_rules', 'assign_conversation', 'assign_campaign'],
    systemPromptHint: 'You are a routing agent. Analyze the conversation and determine the best agent, team, or campaign to assign it to.',
  },
  response: {
    description: 'Handles customer-facing responses using knowledge base, FAQs, and SOPs',
    recommendedTools: ['search_knowledge', 'search_faq', 'search_sop', 'get_campaign_information', 'get_property_information', 'get_conversation_history'],
    systemPromptHint: 'You are a response agent. Search the knowledge base and provide accurate, helpful answers to customer questions.',
  },
  data: {
    description: 'Answers data questions by querying the database and generating reports',
    recommendedTools: ['database_read', 'csv_generator', 'xlsx_generator', 'pdf_generator', 'json_generator'],
    systemPromptHint: 'You are a data analysis agent. Understand natural language data requests and generate SQL queries and reports.',
  },
  translation: {
    description: 'Translates messages between languages for multilingual customer support',
    recommendedTools: ['get_conversation_context'],
    systemPromptHint: 'You are a translation agent. Translate messages accurately between languages while preserving meaning and context.',
  },
  custom: {
    description: 'Build a custom agent with any combination of tools and capabilities',
    recommendedTools: [],
    systemPromptHint: 'You are a custom AI agent. Define your purpose in the system prompt below.',
  },
}

const AGENT_TYPE_META: Record<string, { icon: JSX.Element; iconBg: string; label: string }> = {
  routing: { icon: <Network className="h-4 w-4" />, iconBg: 'bg-blue-100 text-blue-600', label: 'Routing' },
  response: { icon: <Brain className="h-4 w-4" />, iconBg: 'bg-purple-100 text-purple-600', label: 'Response' },
  data: { icon: <Bot className="h-4 w-4" />, iconBg: 'bg-emerald-100 text-emerald-600', label: 'Data' },
  translation: { icon: <FileText className="h-4 w-4" />, iconBg: 'bg-cyan-100 text-cyan-600', label: 'Translation' },
  custom: { icon: <Bot className="h-4 w-4" />, iconBg: 'bg-gray-100 text-gray-600', label: 'Custom' },
}

// -----------------------------------------------------------------------------
// Theme map
// -----------------------------------------------------------------------------

const CATEGORY_META: Record<
  Agent['category'],
  {
    icon: JSX.Element
    iconBg: string
    chipBg: string
    chipText: string
    toggle: string
    border: string
    ring: string
    softBg: string
    text: string
  }
> = {
  Data: {
    icon: <Bot className="h-5 w-5 text-emerald-700" />,
    iconBg: 'bg-emerald-100',
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-800',
    toggle: 'peer-checked:bg-emerald-500',
    border: 'border-emerald-200',
    ring: 'focus:ring-emerald-500',
    softBg: 'bg-emerald-50/50',
    text: 'text-emerald-700',
  },
  Response: {
    icon: <Brain className="h-5 w-5 text-purple-700" />,
    iconBg: 'bg-purple-100',
    chipBg: 'bg-purple-100',
    chipText: 'text-purple-800',
    toggle: 'peer-checked:bg-purple-500',
    border: 'border-purple-200',
    ring: 'focus:ring-purple-500',
    softBg: 'bg-purple-50/50',
    text: 'text-purple-700',
  },
  Routing: {
    icon: <Network className="h-5 w-5 text-blue-700" />,
    iconBg: 'bg-blue-100',
    chipBg: 'bg-blue-100',
    chipText: 'text-blue-800',
    toggle: 'peer-checked:bg-blue-500',
    border: 'border-blue-200',
    ring: 'focus:ring-blue-500',
    softBg: 'bg-blue-50/50',
    text: 'text-blue-700',
  },
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function groupByCategory<T extends { category: string }>(items: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const list = groups.get(item.category) ?? []
    list.push(item)
    groups.set(item.category, list)
  }
  return Array.from(groups.entries())
}

function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onEscape: () => void,
) {
  // Keep the latest onEscape in a ref so the effect doesn't re-run when
  // the parent re-renders and creates a new closure. Without this, every
  // keystroke would re-trigger the effect, calling first?.focus() and
  // stealing focus from the textarea the user is typing in.
  const onEscapeRef = useRef(onEscape)
  onEscapeRef.current = onEscape

  useEffect(() => {
    if (!isOpen) return

    const drawer = ref.current
    if (!drawer) return

    const focusables = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'input, textarea, button, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'))

    // Only focus the first element when the drawer initially opens —
    // not on every effect re-run, which would steal focus from inputs.
    if (document.activeElement === null || !drawer.contains(document.activeElement)) {
      const first = focusables()[0]
      first?.focus()
    }

    function handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onEscapeRef.current()
        return
      }
      if (e.key !== 'Tab' || !drawer) return
      const focusable = focusables()
      if (focusable.length === 0) return
      const firstEl = focusable[0]!
      const lastEl = focusable[focusable.length - 1]!
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
  }, [isOpen, ref])
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function RemovableChip({
  label,
  onRemove,
  chipBg,
  chipText,
}: {
  label: string
  onRemove: () => void
  chipBg: string
  chipText: string
}): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${chipBg} ${chipText}`}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="rounded-full p-0.5 transition-colors hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-inherit"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

function SearchableMultiSelect({
  label,
  assigned,
  all,
  onAdd,
  onRemove,
  onSelectAll,
  onClear,
  chipBg,
  chipText,
  placeholder = 'Search…',
}: {
  label: string
  assigned: string[]
  all: { key: string; label: string; category: string }[]
  onAdd: (key: string) => void
  onRemove: (key: string) => void
  onSelectAll?: () => void
  onClear?: () => void
  chipBg: string
  chipText: string
  placeholder?: string
}): JSX.Element {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q),
    )
  }, [all, query])

  const grouped = groupByCategory(filtered)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</label>
        {(onSelectAll || onClear) && (
          <div className="flex items-center gap-2">
            {onSelectAll && (
              <button
                type="button"
                onClick={onSelectAll}
                className="rounded-lg border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50"
              >
                Select All
              </button>
            )}
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                className="rounded-lg border border-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-50"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-sm border border-gray-300 bg-white py-2 pl-9 pr-3 text-xs text-gray-700 outline-none transition-colors focus:border-[#3454cf] focus:ring-1 focus:ring-[#3454cf]"
          aria-label={`Search ${label.toLowerCase()}`}
        />
      </div>

      {assigned.length > 0 && (
        <div className="text-[10px] text-gray-500">
          <span className="font-medium text-[#3454cf]">{assigned.length}</span> selected
        </div>
      )}

      <div className="flex min-h-[28px] flex-wrap gap-2">
        {assigned.map((key) => {
          const item = all.find((i) => i.key === key)
          if (!item) return null
          return (
            <RemovableChip
              key={key}
              label={item.label}
              onRemove={() => onRemove(key)}
              chipBg={chipBg}
              chipText={chipText}
            />
          )
        })}
      </div>

      <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
        {grouped.length === 0 ? (
          <p className="py-2 text-center text-xs text-gray-400">
            No {label.toLowerCase()} found
          </p>
        ) : (
          grouped.map(([category, items]) => (
            <div key={category} className="mb-2 last:mb-0">
              <p className="sticky top-0 flex items-center gap-1.5 bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                <span className={`rounded-sm px-1.5 py-0.5 text-[9px] font-medium ${
                  category === 'routing' ? 'bg-orange-50 text-orange-600' :
                  category === 'knowledge' ? 'bg-green-50 text-green-600' :
                  category === 'data' ? 'bg-blue-50 text-blue-600' :
                  category === 'file-generation' ? 'bg-yellow-50 text-yellow-700' :
                  category === 'custom' ? 'bg-purple-50 text-purple-600' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {category}
                </span>
                <span className="text-gray-400">{items.length}</span>
              </p>
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const isChecked = assigned.includes(item.key)
                  return (
                    <li key={item.key}>
                      <label
                        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (isChecked) {
                              onRemove(item.key)
                            } else {
                              onAdd(item.key)
                            }
                          }}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                        />
                        <span className="flex-1">{item.label}</span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Main drawer
// -----------------------------------------------------------------------------

export default function EditAgentDrawer({
  mode = 'edit',
  agent,
  isOpen,
  allTools,
  allKnowledge,
  initialTab = 'basic',
  onClose,
  onSave,
  onCreate,
  onDelete,
}: EditAgentDrawerProps): JSX.Element | null {
  const isCreateMode = mode === 'create'
  const drawerRef = useRef<HTMLDivElement>(null)
  const { confirm, Dialog } = useConfirmDialog()
  const [form, setForm] = useState<Agent>(() => deepClone(agent))
  const [initial, setInitial] = useState<Agent>(() => deepClone(agent))
  const [isDirty, setIsDirty] = useState(false)
  const [isSavingAsPrompt, setIsSavingAsPrompt] = useState(false)
  /** Tracks the exact instructions content that was last saved to Prompt
   *  Studio. The Save button stays disabled until the user modifies the
   *  System Instructions field so they don't re-save identical content. */
  const [promptSavedContent, setPromptSavedContent] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<AgentDrawerTab>(initialTab)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // AI prompt generation state
  const [showGenerate, setShowGenerate] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPolishing, setIsPolishing] = useState(false)
  const [genBrief, setGenBrief] = useState<PromptBrief>(DEFAULT_BRIEF)
  const [polishPreview, setPolishPreview] = useState<{ before: string; after: string } | null>(null)
  const [promptSource, setPromptSource] = useState<PromptSource>('inline')
  const [availablePrompts, setAvailablePrompts] = useState<PromptTemplate[]>([])
  const [selectedPromptId, setSelectedPromptId] = useState('')
  const [promptSearch, setPromptSearch] = useState('')

  // Guardrails selected for inclusion when generating an inline prompt with
  // AI. The picker resolves selected guardrail IDs to their content strings
  // and passes them as `guardrailContents` to the generate-prompt API.
  const [selectedGuardrailIds, setSelectedGuardrailIds] = useState<Set<string>>(new Set())
  const [selectedGuardrailContents, setSelectedGuardrailContents] = useState<string[]>([])
  const [initialGuardrailIds, setInitialGuardrailIds] = useState<string[]>([])

  const toast = useToast()
  const theme = CATEGORY_META[form.category]
  const labelClass = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'
  const inputClass = 'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'

  // Reset form when the drawer opens or the agent ID changes.
  // We intentionally depend on `agent.id` (not the whole `agent` object)
  // because the parent creates a new object every render via toDrawerAgent(),
  // which would otherwise reset the form and wipe unsaved edits on every
  // parent re-render.
  const prevOpenRef = useRef(false)
  const prevAgentIdRef = useRef<string | undefined>(agent.id)
  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current
    const agentChanged = isOpen && agent.id !== prevAgentIdRef.current
    if (justOpened || agentChanged) {
      const next = deepClone(agent)
      setForm(next)
      setInitial(next)
      setIsDirty(false)
      setActiveTab(initialTab)
      setSelectedGuardrailIds(new Set())
      setSelectedGuardrailContents([])
      setInitialGuardrailIds([])
      setPromptSavedContent(null)
      setSubmitError(null)
      // Initialize prompt source based on whether the agent has a template
      const hasTemplate = !!(agent.promptId)
      setPromptSource(hasTemplate ? 'template' : 'inline')
      setSelectedPromptId(agent.promptId ?? '')
      // Load guardrails attached to this agent
      if (agent.slug) {
        api.get<{ guardrails: Array<{ guardrailId: string }> }>(`/admin/ai/agents/${agent.slug}/guardrails`)
          .then((res) => {
            const ids = res.guardrails.map((g) => g.guardrailId)
            setSelectedGuardrailIds(new Set(ids))
            setInitialGuardrailIds(ids)
          })
          .catch(() => {})
      }
    }
    prevOpenRef.current = isOpen
    prevAgentIdRef.current = agent.id
  }, [isOpen, agent.id, agent, initialTab])

  useEffect(() => {
    const formChanged = JSON.stringify(form) !== JSON.stringify(initial)
    const guardrailsChanged = JSON.stringify([...selectedGuardrailIds].sort()) !== JSON.stringify([...initialGuardrailIds].sort())
    setIsDirty(formChanged || guardrailsChanged)
  }, [form, initial, selectedGuardrailIds, initialGuardrailIds])

  // Load prompt templates when the drawer opens or prompt tab is activated
  useEffect(() => {
    if (isOpen && activeTab === 'prompt' && availablePrompts.length === 0) {
      api.get<{ prompts: PromptTemplate[] }>('/admin/ai/prompts').then((res) => {
        setAvailablePrompts(res.prompts ?? [])
      }).catch(() => {})
    }
  }, [isOpen, activeTab, availablePrompts.length])

  const handleClose = () => {
    if (isDirty) {
      confirm({
        title: 'Discard Changes',
        message: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep Editing',
        onConfirm: () => onClose(),
      })
      return
    }
    onClose()
  }

  const handleSave = () => {
    if (isCreateMode) {
      void handleSubmit()
    } else {
      onSave?.(form, Array.from(selectedGuardrailIds))
      onClose()
    }
  }

  const handleSubmit = async (): Promise<void> => {
    if (!form.name.trim()) {
      setSubmitError('Name is required')
      setActiveTab('basic')
      return
    }
    if (!form.slug.trim()) {
      setSubmitError('Slug is required')
      setActiveTab('basic')
      return
    }
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      await onCreate?.({
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim(),
        type: form.type,
        systemPrompt: (form.instructions ?? '').trim(),
        promptId: selectedPromptId,
        isEnabled: form.status.enabled,
        tools: form.tools,
        knowledge: form.knowledge,
        guardrailIds: Array.from(selectedGuardrailIds),
      })
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create agent')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveAsPrompt = async () => {
    const promptContent = (form.instructions ?? '').trim()
    if (!promptContent) {
      toast.error('Nothing to save', 'System instructions are empty — nothing to save to Prompt Studio.')
      return
    }
    setIsSavingAsPrompt(true)
    try {
      const promptSlug = `${form.name.trim().toLowerCase().replace(/\s+/g, '-')}-prompt`
      const promptName = `${form.name.trim() || form.id} Prompt`
      await api.post<{ id: string }>('/admin/ai/prompts', {
        slug: promptSlug,
        name: promptName,
        description: `System prompt for ${form.name.trim() || form.id}`,
        category: 'agent',
        content: promptContent,
      })
      toast.success('Saved to Prompt Studio', `Prompt "${promptName}" has been created in the Prompt Studio.`)
      setPromptSavedContent(promptContent)
    } catch (err) {
      toast.error('Failed to save prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setIsSavingAsPrompt(false)
    }
  }

  const handleGenerate = async (): Promise<void> => {
    const hasBrief = genBrief.task.trim() || genBrief.constraints.trim()
    const hasStructured = genBrief.goal.trim() || genBrief.rolePersona.trim() || genBrief.instructions.trim()
    if (!hasBrief && !hasStructured) {
      toast.error('Nothing to generate from', 'Fill in a task, constraints, or a structured field first.')
      return
    }
    setIsGenerating(true)
    try {
      const res = await api.post<{ content: string }>('/admin/ai/agents/generate-prompt', {
        ...(genBrief.task.trim() ? { task: genBrief.task.trim() } : {}),
        ...(genBrief.audience.trim() ? { audience: genBrief.audience.trim() } : {}),
        tone: genBrief.tone,
        ...(genBrief.constraints.trim() ? { constraints: genBrief.constraints.trim() } : {}),
        ...(genBrief.goal.trim() ? { goal: genBrief.goal.trim() } : {}),
        ...(genBrief.rolePersona.trim() ? { rolePersona: genBrief.rolePersona.trim() } : {}),
        ...(genBrief.instructions.trim() ? { instructions: genBrief.instructions.trim() } : {}),
        agentType: form.category.toLowerCase(),
        toolNames: form.tools,
        // Guardrails selected in the picker — appended as a mandatory final
        // layer by the generate-prompt API.
        guardrailContents: selectedGuardrailContents,
      })
      setForm((prev) => ({ ...prev, instructions: res.content }))
      setPromptSource('inline')
      setSelectedPromptId('')
      setShowGenerate(false)
      toast.success('Prompt generated', 'Review and edit the system prompt before saving.')
    } catch (err) {
      toast.error('Failed to generate prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setIsGenerating(false)
    }
  }

  const handlePolish = async (): Promise<void> => {
    const content = (form.instructions ?? '').trim()
    if (!content) {
      toast.error('Nothing to polish', 'Write or generate a system prompt first.')
      return
    }
    setIsPolishing(true)
    try {
      const res = await api.post<{ content: string }>('/admin/ai/agents/polish-prompt', { content })
      setPolishPreview({ before: content, after: res.content })
    } catch (err) {
      toast.error('Failed to polish prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setIsPolishing(false)
    }
  }

  const filteredPrompts = useMemo(() => {
    const q = promptSearch.trim().toLowerCase()
    if (!q) return availablePrompts
    return availablePrompts.filter((p) =>
      p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q) || p.category.toLowerCase().includes(q),
    )
  }, [availablePrompts, promptSearch])

  const handleDelete = () => {
    confirm({
      title: 'Delete Agent',
      message: 'Delete this agent? This action cannot be undone.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: () => {
        onDelete?.(form.id)
        onClose()
      },
    })
  }

  const toolOptions = useMemo(
    () => allTools.map((t) => ({ key: t.name, label: t.name, category: t.category })),
    [allTools],
  )

  const knowledgeOptions = useMemo(
    () => allKnowledge.map((k) => ({ key: k.id, label: k.name, category: k.category })),
    [allKnowledge],
  )

  useFocusTrap(drawerRef, isOpen, handleClose)

  if (!isOpen) return null

  const toggleValue = (field: 'enabled') => (value: boolean) => {
    setForm((prev) => ({
      ...prev,
      status: { ...prev.status, [field]: value },
    }))
  }

  const toggleListItem = (list: 'tools' | 'knowledge', key: string, add: boolean) => {
    setForm((prev) => {
      const current = new Set(prev[list])
      if (add) current.add(key)
      else current.delete(key)
      return { ...prev, [list]: Array.from(current) }
    })
  }

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="edit-agent-title">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl md:w-[800px]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex items-center gap-2">
            {isCreateMode ? <Plus className="h-4 w-4 text-[#3454cf]" /> : <Pencil className="h-4 w-4 text-gray-500" />}
            <h2 className="text-sm font-semibold text-gray-900">{isCreateMode ? 'Create New Agent' : 'Edit Agent'}</h2>
          </div>
          <button
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            onClick={handleClose}
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab navigation */}
        <div className="flex border-b border-gray-200 px-4">
          {([
            { key: 'basic' as const, label: 'Identity', icon: <Bot className="h-3.5 w-3.5" /> },
            { key: 'type' as const, label: 'Type', icon: <Cpu className="h-3.5 w-3.5" /> },
            { key: 'tools' as const, label: 'Tools', icon: <Wrench className="h-3.5 w-3.5" /> },
            { key: 'knowledge' as const, label: 'Knowledge', icon: <Database className="h-3.5 w-3.5" /> },
            { key: 'prompt' as const, label: 'Prompt & Guardrails', icon: <FileText className="h-3.5 w-3.5" /> },
          ]).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors ${
                activeTab === tab.key
                  ? 'border-[#3454cf] text-[#3454cf]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.key === 'tools' && form.tools.length > 0 && (
                <span className="ml-0.5 rounded-full bg-[#3454cf]/10 px-1.5 text-[9px] font-bold text-[#3454cf]">
                  {form.tools.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {submitError && (
          <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-xs text-red-600">
            {submitError}
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4 pb-28">
          {/* ── Tab 1: Basic ── */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">Agent Identity</h4>
                <p className="text-xs text-gray-500">Name and describe this AI agent.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="agent-name" className={labelClass}>
                    Name *
                  </label>
                  <input
                    id="agent-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    className={inputClass}
                    placeholder="Agent name"
                  />
                </div>
                <div>
                  <label htmlFor="agent-slug" className={labelClass}>
                    Slug{isCreateMode ? ' *' : ''}
                  </label>
                  <input
                    id="agent-slug"
                    type="text"
                    value={form.slug}
                    onChange={isCreateMode ? (e) => setForm((prev) => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })) : undefined}
                    readOnly={!isCreateMode}
                    disabled={!isCreateMode}
                    className={`${inputClass} ${isCreateMode ? '' : 'cursor-not-allowed bg-gray-100 text-gray-500'}`}
                    title={isCreateMode ? 'Lowercase letters, numbers, and hyphens only' : 'Slug cannot be changed after creation — it is a stable identifier.'}
                    placeholder="my-agent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="agent-description" className={labelClass}>
                  Description
                </label>
                <textarea
                  id="agent-description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  className={`${inputClass} resize-none`}
                  placeholder="What does this agent do?"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={form.status.enabled}
                  onChange={(e) => toggleValue('enabled')(e.target.checked)}
                  className="h-3.5 w-3.5 rounded-sm border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                />
                <span>Enable agent</span>
              </label>

              {form.status.isSystem && (
                <p className="text-[10px] text-amber-600">
                  This is a system agent. Some fields are read-only.
                </p>
              )}
            </div>
          )}

          {/* ── Tab 2: Type ── */}
          {activeTab === 'type' && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">Agent Type</h4>
                <p className="text-xs text-gray-500">
                  {isCreateMode
                    ? 'Choose a type to pre-configure tools and behavior. You can customize everything in the next steps.'
                    : 'The agent type determines its role and default behavior.'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(AGENT_TYPE_PRESETS).map(([key, preset]) => {
                  const meta = AGENT_TYPE_META[key] ?? AGENT_TYPE_META['custom']!
                  const isSelected = form.type === key
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={isCreateMode ? () => setForm((prev) => ({ ...prev, type: key })) : undefined}
                      disabled={!isCreateMode}
                      className={`rounded-sm border p-4 text-left transition-all ${
                        isSelected
                          ? 'border-[#3454cf] bg-[#3454cf]/5 ring-1 ring-[#3454cf]'
                          : isCreateMode
                            ? 'border-gray-200 bg-white hover:border-gray-300'
                            : 'border-gray-200 bg-white cursor-not-allowed'
                      }`}
                      title={!isCreateMode ? 'Type cannot be changed after creation — it determines the agent hierarchy.' : undefined}
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <div className={`flex h-7 w-7 items-center justify-center rounded-sm ${meta.iconBg}`}>
                          {meta.icon}
                        </div>
                        <span className="text-sm font-semibold text-gray-900 capitalize">{preset.description.split('.')[0]}</span>
                      </div>
                      <p className="text-xs leading-relaxed text-gray-500">{preset.description}</p>
                      {preset.recommendedTools.length > 0 && (
                        <p className="mt-2 text-[10px] font-medium text-gray-400">
                          {preset.recommendedTools.length} recommended tools
                        </p>
                      )}
                    </button>
                  )
                })}
              </div>
              {!isCreateMode && (
                <p className="text-[10px] text-amber-600">
                  Type cannot be changed after creation — it determines the agent hierarchy and routing behavior.
                </p>
              )}
            </div>
          )}

          {/* ── Tab 3: Tools ── */}
          {activeTab === 'tools' && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">Capabilities &amp; Tools</h4>
                <p className="text-xs text-gray-500">Select which tools this agent can use.</p>
              </div>
              <SearchableMultiSelect
                label="Tools"
                assigned={form.tools}
                all={toolOptions}
                chipBg={theme.chipBg}
                chipText={theme.chipText}
                onAdd={(key) => toggleListItem('tools', key, true)}
                onRemove={(key) => toggleListItem('tools', key, false)}
                onSelectAll={() => setForm((prev) => ({ ...prev, tools: toolOptions.map((t) => t.key) }))}
                onClear={() => setForm((prev) => ({ ...prev, tools: [] }))}
                placeholder="Search tools by name or category…"
              />
            </div>
          )}

          {/* ── Tab 3: Knowledge ── */}
          {activeTab === 'knowledge' && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">Knowledge Collections</h4>
                <p className="text-xs text-gray-500">Select which knowledge collections this agent can search.</p>
              </div>
              <SearchableMultiSelect
                label="Knowledge Sources"
                assigned={form.knowledge}
                all={knowledgeOptions}
                chipBg={theme.chipBg}
                chipText={theme.chipText}
                onAdd={(key) => toggleListItem('knowledge', key, true)}
                onRemove={(key) => toggleListItem('knowledge', key, false)}
                onSelectAll={() => setForm((prev) => ({ ...prev, knowledge: knowledgeOptions.map((k) => k.key) }))}
                onClear={() => setForm((prev) => ({ ...prev, knowledge: [] }))}
                placeholder="Search knowledge sources…"
              />
            </div>
          )}

          {/* ── Tab 4: Prompt ── */}
          {activeTab === 'prompt' && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">System Prompt</h4>
                <p className="text-xs text-gray-500">Define the agent's behavior, role, tone, and rules.</p>
              </div>

              {/* Prompt source toggle — identical to orchestrator form */}
              <div
                role="radiogroup"
                aria-label="Prompt source"
                className="mb-2 grid grid-cols-2 gap-1 rounded-sm bg-gray-100 p-1"
              >
                {([
                  { value: 'inline' as const, label: 'Write inline', icon: <FileText className="h-3 w-3" /> },
                  { value: 'template' as const, label: 'Use a template', icon: <Bot className="h-3 w-3" /> },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={promptSource === opt.value}
                    onClick={() => setPromptSource(opt.value)}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-semibold transition-colors ${
                      promptSource === opt.value
                        ? 'bg-white text-[#3454cf] shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Inline prompt mode */}
              {promptSource === 'inline' ? (
                <div>
                  {/* Guardrail selection — available when writing an inline
                      prompt, whether manually or via AI generation. Selected
                      guardrails are passed as `guardrailContents` to the
                      generate-prompt API. */}
                  <div className="mb-3">
                    <GuardrailPicker
                      selectedIds={selectedGuardrailIds}
                      onChange={(ids, contents) => {
                        setSelectedGuardrailIds(ids)
                        setSelectedGuardrailContents(contents)
                      }}
                    />
                  </div>
                  <div className="mb-1 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowGenerate(!showGenerate)}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline"
                    >
                      <Sparkles className="h-3 w-3" />
                      {showGenerate ? 'Hide' : 'Generate with AI'}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handlePolish()}
                      disabled={isPolishing || !(form.instructions ?? '').trim()}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline disabled:opacity-40"
                    >
                      {isPolishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                      Polish with AI
                    </button>
                  </div>

                  {showGenerate && (
                    <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                      <div className="space-y-3">
                        <div>
                          <label className={labelClass}>What should this prompt do?</label>
                          <textarea
                            value={genBrief.task}
                            onChange={(e) => setGenBrief((p) => ({ ...p, task: e.target.value }))}
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="e.g. Answer customer questions using the knowledge base"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={labelClass}>Audience</label>
                            <input
                              type="text"
                              value={genBrief.audience}
                              onChange={(e) => setGenBrief((p) => ({ ...p, audience: e.target.value }))}
                              className={inputClass}
                              placeholder="e.g. Customer support"
                            />
                          </div>
                          <div>
                            <label className={labelClass}>Tone</label>
                            <Select
                              ariaLabel="Tone"
                              value={genBrief.tone}
                              onChange={(v) => setGenBrief((p) => ({ ...p, tone: v as PromptBrief['tone'] }))}
                            >
                              <option value="professional">Professional</option>
                              <option value="friendly">Friendly</option>
                              <option value="concise">Concise</option>
                              <option value="detailed">Detailed</option>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setShowGenerate(false)}
                            className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleGenerate()}
                            disabled={isGenerating || !genBrief.task.trim()}
                            className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                          >
                            {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                            Generate
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Polish preview — before/after with accept/reject */}
                  {polishPreview && (
                    <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#3454cf]">Polish preview</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setPolishPreview(null)}
                            className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                          >
                            Reject
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, instructions: polishPreview.after }))
                              setPolishPreview(null)
                              toast.success('Prompt polished', 'The improved prompt has been applied.')
                            }}
                            className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0]"
                          >
                            <Check className="h-3 w-3" />
                            Accept
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Before</span>
                          <pre className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-sm bg-white p-2 text-[10px] text-gray-600">{polishPreview.before}</pre>
                        </div>
                        <div>
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#3454cf]">After</span>
                          <pre className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-sm bg-white p-2 text-[10px] text-gray-700">{polishPreview.after}</pre>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="agent-instructions" className={labelClass}>
                      System Instructions
                    </label>
                    <textarea
                      id="agent-instructions"
                      rows={10}
                      value={form.instructions ?? ''}
                      onChange={(e) => setForm((prev) => ({ ...prev, instructions: e.target.value }))}
                      className={`${inputClass} resize-none`}
                      placeholder="Optional behavioral instructions for this agent…"
                    />
                    <p className="mt-1 text-[10px] text-gray-400">
                      This prompt is sent to the AI model on every interaction.
                    </p>
                  </div>

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={handleSaveAsPrompt}
                      disabled={isSavingAsPrompt || !(form.instructions ?? '').trim() || promptSavedContent === (form.instructions ?? '').trim()}
                      className="inline-flex items-center gap-1 rounded bg-[#3454cf] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[#2a45b0] disabled:opacity-50"
                      title={promptSavedContent === (form.instructions ?? '').trim() && promptSavedContent !== null ? 'No changes since last save — modify the instructions to re-enable.' : undefined}
                    >
                      {isSavingAsPrompt ? 'Saving…' : 'Save to Prompt Studio'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Template picker mode */
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={promptSearch}
                      onChange={(e) => setPromptSearch(e.target.value)}
                      placeholder="Search prompt templates…"
                      className={`${inputClass} pl-9`}
                    />
                  </div>
                  <div className="max-h-80 space-y-1 overflow-y-auto">
                    {filteredPrompts.length === 0 ? (
                      <p className="py-4 text-center text-xs text-gray-400">No prompt templates found.</p>
                    ) : (
                      filteredPrompts.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setSelectedPromptId(p.id)
                            setForm((prev) => ({ ...prev, instructions: p.content, promptId: p.id }))
                          }}
                          className={`w-full rounded-md border px-3 py-2 text-left transition-colors ${
                            selectedPromptId === p.id
                              ? 'border-[#3454cf] bg-[#3454cf]/5'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-900">{p.name}</span>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-medium text-gray-500">{p.category}</span>
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-[10px] text-gray-500">{p.content}</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            {/* Left: Back button or Delete */}
            {activeTab !== AGENT_TAB_ORDER[0] ? (
              <button
                type="button"
                onClick={() => {
                  const idx = AGENT_TAB_ORDER.indexOf(activeTab)
                  if (idx > 0) setActiveTab(AGENT_TAB_ORDER[idx - 1]!)
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            ) : !isCreateMode && !form.status.isSystem && onDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center gap-1.5 text-xs font-medium text-red-600 transition-colors hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete Agent
              </button>
            ) : (
              <span />
            )}
            {/* Right: Cancel + Next/Save */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-50"
              >
                Cancel
              </button>
              {activeTab !== AGENT_TAB_ORDER[AGENT_TAB_ORDER.length - 1] ? (
                <button
                  type="button"
                  onClick={() => {
                    const idx = AGENT_TAB_ORDER.indexOf(activeTab)
                    if (idx < AGENT_TAB_ORDER.length - 1) setActiveTab(AGENT_TAB_ORDER[idx + 1]!)
                  }}
                  disabled={isCreateMode && (!form.name.trim() || !form.slug.trim())}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                    isCreateMode && (!form.name.trim() || !form.slug.trim())
                      ? 'cursor-not-allowed bg-gray-300'
                      : `bg-[#3454cf] hover:bg-[#2a45b0] ${theme.ring}`
                  }`}
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isCreateMode ? isSubmitting || !form.name.trim() || !form.slug.trim() : isSubmitting || !isDirty}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                    isCreateMode
                      ? isSubmitting || !form.name.trim() || !form.slug.trim()
                        ? 'cursor-not-allowed bg-gray-300'
                        : `bg-[#3454cf] hover:bg-[#2a45b0] ${theme.ring}`
                      : isSubmitting || !isDirty
                        ? 'cursor-not-allowed bg-gray-300'
                        : `bg-[#3454cf] hover:bg-[#2a45b0] ${theme.ring}`
                  }`}
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isCreateMode ? <Plus className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                  {isCreateMode ? 'Create Agent' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog />
    </div>
  )
}
